import React, { useState } from 'react';
import { signInWithEmailAndPassword, UserCredential } from 'firebase/auth';
import { auth, db } from '../../firebaseConfig';
import { Formik, Form } from 'formik';
import { z } from 'zod';
import './LoginPage.css';
import FormInput from '../../components/FormInput/FormInput';

import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { doc, getDoc } from 'firebase/firestore';
import { CollectionEnum } from '../../Types/Common.type';
import { User } from '../../Types/User.type';
import { Commerce } from '../../Types/Commerce.type';
import { useAppContext } from '../../AppContext';

const LoginPage: React.FC = () => {

  const { setUser, setCommerce } = useAppContext();
  const [ , setLoginError] = useState<string | null>(null);
  const [ , setUserCredential] = useState<UserCredential | null>(null);
  const initialValues = {
    email: '',
    password: ''
  }

  const validationSchema = z.object({
    email: z.string().email('El formato de correo es incorrecto').min(1, 'El correo es requerido'),
    password: z.string().min(8, 'La contraseña debe tener al menos 8 caracteres').min(1, 'La contraseña es requerida')
  })

  const validateForm = (values: { email: string; password: string }) => {
    try {
      validationSchema.parse(values);
      return {};
    } catch (error) {
      if (error instanceof z.ZodError) {
        const errors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path) {
            errors[err.path[0]] = err.message;
          }
        });
        return errors;
      }
      return {};
    }
  };

  const handleSubmit = async (
    values: { email: string; password: string },
    { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
  ) => {
    setSubmitting(true);
    setLoginError(null); // Clear any previous errors

    try {
      // Sign in with email and password
      const userCredential = await signInWithEmailAndPassword(auth, values.email, values.password);
      setUserCredential(userCredential);
      
      // Get user from firestore
      const user = userCredential.user;
      const userDocRef = doc(db, CollectionEnum.USERS, user.uid);
      const userSnap = await getDoc(userDocRef);
      if (!userSnap.exists()) {
        throw new Error('Usuario no encontrado');
      }
      const userData = userSnap.data() as User;
      setUser(userData);
      // Get Commerce
      const commerceId = userData.commerce_id;
      const commerceDocRef = doc(db, CollectionEnum.COMMERCES, commerceId);
      const commerceSnap = await getDoc(commerceDocRef);
      if (!commerceSnap.exists()) {
        throw new Error('Comercio no encontrado');
      }
      const commerceData = commerceSnap.data() as Commerce;
      setCommerce(commerceData);
      
      // Navigation will be handled automatically by the App component
    } catch (error: any) {
      console.error('Error al iniciar sesión:', error);
      let errorMessage = 'Error al iniciar sesión. Por favor, inténtalo de nuevo.';
      if (error.code === 'auth/user-not-found') {
        errorMessage = 'Usuario no encontrado. Verifica tu correo electrónico.';
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = 'Contraseña incorrecta. Inténtalo de nuevo.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'El formato del correo electrónico no es válido.';
      }
      toast.error(errorMessage, {
        position: 'top-right',
        autoClose: 5000
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-container center">
      <div className="card-container">
        <div className="brand center">
          <div className="brand-logo-container">
            <img src="/biyuyo-logo-full.jpeg" alt="Biyuyo Logo" className="brand-logo-image desktop-logo" />
            <img src="/biyuyo-dog-logo.jpeg" alt="Biyuyo Dog Logo" className="brand-logo-image mobile-logo" />
          </div>
          <img src="/biyuyo-text-logo.png" alt="Biyuyo Text" className="brand-text-corner" />
        </div>
        <div className="login-form">
          <div className='title'>Iniciar Sesión</div>
          <div className='subtitle'>Iniciar sesión ahora es más fácil y seguro que nunca.</div>
          <Formik
            initialValues={initialValues}
            validate={validateForm}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, values, handleChange, errors, isValid }) => (
              <Form>
                <FormInput
                  name="email"
                  type="email"
                  label="Correo Electrónico"
                  placeholder="Introduce tu correo electrónico"
                  form={values}
                  onTextChange={handleChange}
                  required
                  errors={errors}
                />

                <FormInput
                  name="password"
                  type="password"
                  label="Contraseña"
                  placeholder="Introduce tu contraseña"
                  form={values}
                  onTextChange={handleChange}
                  required
                  errors={errors}
                />

                <button type="submit" disabled={isSubmitting || !isValid}>
                  {isSubmitting ? 'Iniciando sesión...' : 'Iniciar Sesión'}
                </button>
              </Form>
            )}
          </Formik>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default LoginPage;